import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Award, Crown, Star } from 'lucide-react';
export function HeroSection() {
  return <section className="relative overflow-hidden bg-gradient-to-b from-brand-offwhite via-white to-brand-cream/30 pt-16 pb-20 px-4 sm:px-6 lg:px-8" data-id="element-320">
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02]" data-id="element-321">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 2px 2px, #2B4C7E 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-322" />
      </div>

      {/* Gradient orbs */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-brand-yellow/10 to-brand-orange/5 rounded-full blur-3xl" data-id="element-323" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-brand-red/10 to-brand-navy/5 rounded-full blur-3xl" data-id="element-324" />

      <div className="max-w-7xl mx-auto relative z-10" data-id="element-325">
        {/* Premium badge */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-326">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-brand-yellow via-brand-orange to-brand-yellow bg-[length:200%_100%] rounded-full text-brand-navy font-bold text-sm shadow-xl border-2 border-white/50" style={{
          animation: 'shimmer 3s ease-in-out infinite'
        }} data-id="element-327">
            <Crown className="w-4 h-4" data-id="element-328" />
            <span className="tracking-wider" data-id="element-329">EDICIÓN PREMIUM</span>
            <Sparkles className="w-4 h-4" data-id="element-330" />
          </div>
        </motion.div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto" data-id="element-331">
          {/* Left: Image */}
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7
        }} className="relative order-2 lg:order-1" data-id="element-332">
            <div className="relative" data-id="element-333">
              <div className="absolute -inset-4 bg-gradient-to-br from-brand-yellow/20 via-brand-orange/15 to-brand-red/20 rounded-2xl blur-xl" data-id="element-334" />

              <div className="relative bg-white p-3 rounded-xl shadow-2xl" data-id="element-335">
                <img src="https://cdn.magicpatterns.com/uploads/nLyyq4PBU7a3VB9hrDYnez/portada_plan_antipantalla.png" alt="Plan Anti-Pantallas Express" className="w-full rounded-lg" data-id="element-336" />

                <motion.div initial={{
                opacity: 0,
                scale: 0.8,
                rotate: -12
              }} animate={{
                opacity: 1,
                scale: 1,
                rotate: -12
              }} transition={{
                delay: 0.4
              }} className="absolute -top-4 -right-4 bg-brand-red text-white px-4 py-2 rounded-full shadow-xl font-bold text-xs border-3 border-white" data-id="element-337">
                  <div className="flex items-center gap-1.5" data-id="element-338">
                    <Award className="w-4 h-4" data-id="element-339" />
                    <span data-id="element-340">Calidad Premium</span>
                  </div>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.6
              }} className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-brand-navy text-white px-4 py-1.5 rounded-full shadow-lg flex items-center gap-1" data-id="element-341">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-brand-yellow text-brand-yellow" data-id="element-342" />)}
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="order-1 lg:order-2 text-center lg:text-left" data-id="element-343">
            <h1 className="text-4xl md:text-6xl font-bold text-brand-navy mb-4 font-serif leading-tight" data-id="element-344">
              Llegó ese momento del día.
              <br data-id="element-345" />
              <span className="text-brand-red italic" data-id="element-346">Necesitas calma.</span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-600 mb-6 leading-relaxed font-light" data-id="element-347">
              Pero hay ruido. Urgencia. Un niño con energía desbordada.
              <span className="block text-brand-navy font-normal mt-2" data-id="element-348">
                Y tú... estás a punto de prender la pantalla.
              </span>
            </p>

            <div className="bg-gradient-to-r from-brand-yellow/10 to-brand-orange/10 p-5 rounded-xl border-l-4 border-brand-yellow mb-6" data-id="element-349">
              <p className="text-brand-navy font-medium italic text-lg" data-id="element-350">
                "Hay otra forma. Un sistema probado que transforma el caos en 20
                minutos de paz predecible."
              </p>
            </div>

            {/* Trust indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-351">
              {[{
              number: '500+',
              label: 'Familias'
            }, {
              number: '30',
              label: 'Actividades'
            }, {
              number: '10 seg',
              label: 'Inicio'
            }, {
              number: '100%',
              label: 'Sin Pantallas'
            }].map((stat, i) => <div key={i} className="bg-white/60 backdrop-blur-sm p-3 rounded-lg border border-brand-navy/10 shadow-sm text-center" data-id="element-352">
                  <div className="text-2xl font-bold text-brand-red font-serif" data-id="element-353">
                    {stat.number}
                  </div>
                  <div className="text-xs text-slate-600 font-medium" data-id="element-354">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>

      <style jsx data-id="element-355">{`
        @keyframes shimmer {
          0%,
          100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
      `}</style>
    </section>;
}